import { FC, useCallback, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Toggle } from "components/inputs/Toggle"

import { settingsService } from "services/settings/settings.service"
import { VerificationFieldsSettings } from "services/settings/type"

import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface VerificationProfileTabProps {
  verification_options: VerificationFieldsSettings
  onChange: (verification_options: VerificationFieldsSettings) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const VerificationProfileTab: FC<VerificationProfileTabProps> = ({
  verification_options,
  onChange,
  refreshSettings,
  onSaved
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [isSaving, setIsSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  const sortedFields = Object.keys(verification_options).sort((a, b) => a.localeCompare(b))

  const handleToggle = useCallback(
    (field: string, toggleType: "required" | "enabled") => {
      onChange({
        ...verification_options,
        [field]: {
          ...verification_options[field],
          [toggleType]: !verification_options[field][toggleType]
        }
      })
    },
    [verification_options, onChange]
  )

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const success = await settingsService.updateVerificationFields(verification_options)
      if (success) {
        toast.success(t("settings.verificationProfile.updated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.tabs.verificationProfile")}</h3>
        <SettingsHistoryNote target="verification_options" refreshKey={historyKey} />
      </div>

      <p className="bg-warning/20 border border-warning/40 px-4 py-3 rounded-lg text-sm">{t("settings.verificationProfile.warning")}</p>
      <p className="bg-info/20 border border-info/40 px-4 py-3 rounded-lg text-sm">{t("settings.verificationProfile.hardcodeInfo")}</p>

      <div className="rounded-lg border border-black/20 dark:border-white/10 bg-white dark:bg-transparent overflow-hidden">
        <div className="grid grid-cols-3 bg-black/[0.03] dark:bg-white/[0.04] border-b border-black/10 dark:border-white/10 px-4 py-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{t("settings.verificationProfile.field")}</span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-center">
            {t("settings.verificationProfile.required")}
          </span>
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase text-center">
            {t("settings.verificationProfile.enabled")}
          </span>
        </div>

        {sortedFields.map((field, index) => {
          const setting = verification_options[field]
          return (
            <div
              key={field}
              className={`grid grid-cols-3 items-center px-4 py-3 border-b border-black/10 dark:border-white/10 last:border-b-0 ${
                index % 2 === 1 ? "bg-black/[0.03] dark:bg-white/[0.04]" : ""
              }`}
            >
              <span className="text-base font-semibold text-gray-800 dark:text-white capitalize">
                {field.replace(/([A-Z])/g, " $1").trim()}
              </span>
              <div className="flex justify-center">
                <Toggle checked={setting.required} onChange={() => handleToggle(field, "required")} disabled={isSaving} />
              </div>
              <div className="flex justify-center">
                <Toggle checked={setting.enabled} onChange={() => handleToggle(field, "enabled")} disabled={isSaving} />
              </div>
            </div>
          )
        })}
      </div>

      <div className="mb-10">
        <SettingsSectionActions
          onSave={handleSave}
          saving={isSaving}
          resetTarget="verification_options"
          resetLabel={t("settings.tabs.verificationProfile")}
          onResetDone={handleResetDone}
        />
      </div>
    </section>
  )
}
